import { Grid2, Icon, Label, Scrollbar } from '@aibees/shared/components';
import { useResponsive } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Tooltip, Typography } from '@mui/material';
import { memo, useMemo, useState } from 'react';
import { useGroupedItemsByType } from '../../hooks/use-grouped-items-by-type';
import { useTransferListItems } from '../../hooks/use-transfer-list-items';
import * as TransferListSX from '../../sx';
import { TransferItem } from '../transfer-item';
import * as Styled from './styles';
import * as SX from './sx';
import type { TransferListExcludedProps as Props } from './types';

const TOOLTIP_POPPER_PROPS = { disablePortal: true };
const TOOLTIP_SLOT_PROPS = {
  popper: {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, -20],
        },
      },
    ],
  },
};
const ACCORDION_SLOT_PROPS = { transition: { timeout: 0 } };

export const TransferListExcluded = memo(
  ({ className, title, itemIcon, tooltipTitle }: Props) => {
    const { all, excluded, included, groupedItems } = useTransferListItems();
    const [openGroup, setOpenGroup] = useState<string | null>(null);
    const mdDown = useResponsive('down', 'md');

    const data = useGroupedItemsByType(
      all.items.filter((x) => excluded.ids?.includes(x.id))
    );

    const [expandedType, setExpandedType] = useState<string | null>(null);

    const handleExpand = (type: string) => {
      setExpandedType((prevType) => (prevType === type ? null : type));
    };

    const handleTooltipClose = () => {
      setOpenGroup(null);
    };

    const handleMouseEnter = (groupLabel: string) => {
      setOpenGroup(groupLabel);
    };

    const handleMouseLeave = () => {
      setOpenGroup(null);
    };

    const detailWrapperHeight = useMemo(() => {
      const scrollbarHeight = groupedItems.length > 1 ? 35.25 : 36;
      const summaryHeight = 4;
      const accordionCount = groupedItems.length;

      return scrollbarHeight - accordionCount * summaryHeight;
    }, [groupedItems.length]);

    return (
      <Grid2
        lg={6}
        xl={6}
        md={6}
        sm={12}
        sx={TransferListSX.leftContent(mdDown)}
      >
        <Grid2 container sx={TransferListSX.columnHeader}>
          <Grid2>
            <Typography variant="body3" fontWeight={600} color="grey.700">
              {title}
            </Typography>
          </Grid2>

          <Label
            variant="filled"
            color="primary"
            size="xxsmall"
            children={excluded.ids.length}
          />
        </Grid2>

        <Grid2 sx={SX.root}>
          {groupedItems.map((group, index) => {
            const groupData = data?.[group.label];
            const itemsLength = groupData?.items?.length || 0;

            const isExpanded = expandedType === group.label;

            return (
              <Styled.Wrapper
                className={classnames('TransferListExcluded', className)}
                key={index}
                sx={SX.transferListExcluded(isExpanded && !!itemsLength)}
                onMouseEnter={() =>
                  itemsLength === 0 && handleMouseEnter(group.label)
                }
                onMouseLeave={handleMouseLeave}
              >
                <Tooltip
                  key={index}
                  title={itemsLength === 0 ? tooltipTitle : undefined}
                  disableFocusListener
                  disableTouchListener
                  PopperProps={TOOLTIP_POPPER_PROPS}
                  onClose={handleTooltipClose}
                  open={itemsLength === 0 && openGroup === group.label}
                  disableHoverListener
                  slotProps={TOOLTIP_SLOT_PROPS}
                >
                  <Styled.CustomAccordion
                    expanded={isExpanded}
                    slotProps={ACCORDION_SLOT_PROPS}
                    key={index}
                  >
                    <Styled.CustomSummary
                      onClick={() => itemsLength && handleExpand(group.label)}
                      expandIcon={
                        <Icon
                          name="arrow-tree-right"
                          size={18}
                          color={
                            itemsLength
                              ? group.groupColor ?? 'primary.500'
                              : 'grey.400'
                          }
                        />
                      }
                      sx={SX.summary(isExpanded && !!itemsLength)}
                    >
                      <Grid2
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                      >
                        <Grid2 direction="row" alignItems="center" gap={0.5}>
                          {itemIcon && (
                            <Icon
                              name="plus-circle"
                              size={16}
                              sxx={SX.itemIcon(isExpanded)}
                            />
                          )}

                          {group.icon && (
                            <Icon
                              name={group.icon}
                              size={18}
                              color={
                                itemsLength ? group.groupColor : 'grey.400'
                              }
                            />
                          )}

                          <Typography
                            variant="body3"
                            fontWeight={400}
                            color={
                              itemsLength
                                ? 'grey.700'
                                : (p) => `${p.palette.grey[400]} !important`
                            }
                          >
                            {group.label}
                          </Typography>
                        </Grid2>

                        <Label
                          variant="soft"
                          color={itemsLength ? 'primary' : 'default'}
                          size="xxsmall"
                          children={itemsLength}
                        />
                      </Grid2>
                    </Styled.CustomSummary>
                    {!!itemsLength && (
                      <Scrollbar
                        sx={{
                          height: (p) => p.spacing(detailWrapperHeight),
                        }}
                      >
                        <Styled.DetailWrapper>
                          {groupData?.items.map((x, i) => (
                            <TransferItem
                              key={i}
                              item={x}
                              includedIds={included.ids}
                              excludedIds={excluded.ids}
                            />
                          ))}
                        </Styled.DetailWrapper>
                      </Scrollbar>
                    )}
                  </Styled.CustomAccordion>
                </Tooltip>
              </Styled.Wrapper>
            );
          })}
        </Grid2>
      </Grid2>
    );
  }
);

TransferListExcluded.displayName = 'TransferListExcluded';
